import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { agnostify } from '@sainsburys-tech/boltui-utils'
import { Image } from '@sainsburys-tech/bolt'
import { ThemeContext } from 'styled-components'
import { onLinkClickFn } from '../../../helpers/ComponentHelper'
import * as Styled from './M020.styles'

/**
 * - M020 is a flat image banner component.
 * - This component is to be used exclusively in cases where a partner/supplier promo must comply with their branding guidelines, meaning that the text must be flattened as part of the image.
 * - In this case, the creative team will have to provide 2 assets: one for mobile and one for desktop/tablet.
 */
const M020 = ({ altText, desktopImage, linkTarget, linkTitle, linkUrl, mobileImage, onLinkClick, tabletImage }) => {
  // Fetch the styled-components theme from context
  const theme = useContext(ThemeContext)
  // Get the breakpoint for switching from mobile to tablet/desktop so we know what image to display
  const BreakpointTablet = theme.size.breakpoint['max-width'].xs
  const breakpointDesktop = theme.size.breakpoint['max-width'].sm
  // If an image URL is provided for smaller viewports, form an array of additional media.
  // Omit if not provided, and the image will fallback to using the main image for all viewports.
  const additionalMedia = [
    { src: mobileImage, query: `(max-width: ${BreakpointTablet})` },
    { src: tabletImage, query: `(max-width: ${breakpointDesktop})` }
  ]

  return (
    <Styled.Wrapper
      data-test='m020-container'
      gutterValue={0}
      href={agnostify(linkUrl)}
      target={linkTarget}
      title={linkTitle}
      onClick={(evt) => onLinkClickFn(evt, onLinkClick, linkUrl)}
      forwardedAs='a'
    >
      <Image data-test='m020-image' src={desktopImage} additionalMedia={additionalMedia} alt={altText} responsive />
    </Styled.Wrapper>
  )
}

M020.propTypes = {
  /** Required alternate text for the image */
  altText: PropTypes.string.isRequired,
  /** Required image for desktop screen size */
  desktopImage: PropTypes.string.isRequired,
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  /** Required link title, can be seen when hovering over the component */
  linkTitle: PropTypes.string.isRequired,
  /** Required link url */
  linkUrl: PropTypes.string.isRequired,
  /** Required image for mobile screen size */
  mobileImage: PropTypes.string.isRequired,
  /** Required image for tablet screen size */
  tabletImage: PropTypes.string.isRequired
}

export default M020
